/**
 * Samples Integration API Service
 * 
 * This module provides API methods for the standalone sample request forms.
 * It connects to the integrations-samples backend for OTP verification,
 * form data retrieval, and form submission.
 */

import axios from "axios";

// Base URL for the samples integration API
// This should be configured via environment variable in production
const SAMPLES_API_BASE_URL = process.env.REACT_APP_SAMPLES_API_URL || "https://integrations-samples-api.dev.impiricus.com";

// Create axios instance for samples API
const samplesApi = axios.create({
  baseURL: SAMPLES_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Types for API responses and requests
export interface StateLicense {
  licNumber: string;
  licExpirationDate?: string;
}

export interface Address {
  addressId: number;
  street: string;
  suite?: string;
  city: string;
  state: string;
  zipCode: string;
  stateLicense?: StateLicense;
}

export interface HCPInfo {
  firstName: string;
  lastName: string;
  npi: string;
  stateLicenseNumber?: string;
  credentials?: string;
  designation?: string;
  phoneNumber?: string;
}

export interface OrderItem {
  totalQuantity: string;
  productGroup: string;
  genericName: string;
  productDescription: string;
  ndc: string;
}

export interface Decision {
  eligible: boolean;
  drugName?: string;
  products?: OrderItem[];
  reasons?: string[];
}

export interface FormData {
  hcp: HCPInfo;
  addresses: Address[];
  decision: Decision;
  requestInfo: {
    requestNumber?: string;
    requestDate?: string;
    conversationID?: string;
  };
  eligible: boolean;
}

const PRODUCT_CATALOG: Record<string, OrderItem[]> = {
  "1": [
    {
      totalQuantity: "6",
      productGroup: "Toujeo\u00AE Max",
      genericName: "Insulin Glargine 300U/mL",
      productDescription: "Toujeo\u00AE Max SoloStar\u00AE 1X3mL Prefilled Pen",
      ndc: "0024587101",
    },
  ],
  "2": [
    {
      totalQuantity: "3",
      productGroup: "Toujeo\u00AE",
      genericName: "Insulin Glargine 300U/mL",
      productDescription: "Toujeo\u00AE SoloStar\u00AE 1X1.5mL Prefilled Pen",
      ndc: "0024586900",
    },
  ],
  "3": [
    {
      totalQuantity: "6",
      productGroup: "Toujeo\u00AE Max",
      genericName: "Insulin Glargine 300U/mL",
      productDescription: "Toujeo\u00AE Max SoloStar\u00AE 1X3mL Prefilled Pen",
      ndc: "0024587101",
    },
    {
      totalQuantity: "3",
      productGroup: "Toujeo\u00AE",
      genericName: "Insulin Glargine 300U/mL",
      productDescription: "Toujeo\u00AE SoloStar\u00AE 1X1.5mL Prefilled Pen",
      ndc: "0024586900",
    },
  ],
};

/**
 * Resolve the decision's drugName to full product details from the catalog.
 * Falls back to decision.products if drugName is not in the catalog.
 */
export function resolveProducts(decision: Decision): OrderItem[] {
  if (decision.drugName && PRODUCT_CATALOG[decision.drugName]) {
    return PRODUCT_CATALOG[decision.drugName];
  }
  return decision.products || [];
}

export interface Signature {
  fullName: string;
  signedAt: string;
  agreedToTerms: boolean;
}

export interface RequestStatus {
  requestID: string;
  conversationID: string;
  otpStatus: string;
  formStatus: string;
  isClosed: boolean;
  available: boolean;
}

// API Response types
interface ApiResponse<T> {
  status: "success" | "error";
  message?: string;
  data?: T;
}

interface SendOTPResponse {
  status: string;
  requestID: string;
  message: string;
  sent: boolean;
  maskedPhone?: string;
}

interface VerifyOTPResponse {
  status: string;
  requestID: string;
  conversationID: string;
  message: string;
  verified: boolean;
}

interface FormDataResponse {
  status: string;
  requestID: string;
  conversationID: string;
  formData: FormData;
}

interface SubmitFormResponse {
  status: string;
  requestID: string;
  conversationID: string;
  message: string;
  submittedAt: string;
}

interface RequestStatusResponse {
  status: string;
  requestID: string;
  conversationID: string;
  otpStatus: string;
  formStatus: string;
  isClosed: boolean;
  available: boolean;
  maskedPhone?: string;
}

/**
 * Send OTP to the phone number associated with the request
 * Uses the existing /medvantx/send-otp endpoint
 */
export async function sendOTP(requestID: string): Promise<SendOTPResponse> {
  try {
    const response = await samplesApi.post<SendOTPResponse>("/medvantx/send-otp", {
      requestID,
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to send OTP");
    }
    throw new Error("Network error. Please try again.");
  }
}

/**
 * Resend OTP to the phone number associated with the request
 * Uses the existing /medvantx/resend-otp endpoint
 */
export async function resendOTP(requestID: string): Promise<SendOTPResponse> {
  try {
    const response = await samplesApi.post<SendOTPResponse>("/medvantx/resend-otp", {
      requestID,
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to resend OTP");
    }
    throw new Error("Network error. Please try again.");
  }
}

/**
 * Verify the OTP entered by the user
 * Uses the existing /medvantx/verify-otp endpoint
 */
export async function verifyOTP(requestID: string, otp: string): Promise<VerifyOTPResponse> {
  try {
    const response = await samplesApi.post<VerifyOTPResponse>("/medvantx/verify-otp", {
      requestID,
      otp,
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Invalid OTP");
    }
    throw new Error("Network error. Please try again.");
  }
}

/**
 * Get form data for populating the sample request form
 * Uses the /medvantx/form-data/<requestID> endpoint
 */
export async function getFormData(requestID: string): Promise<FormDataResponse> {
  try {
    const response = await samplesApi.get<FormDataResponse>(`/medvantx/form-data/${requestID}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("Form not found or has expired");
    }
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to load form data");
    }
    throw new Error("Network error. Please try again.");
  }
}

/**
 * Submit the completed sample request form
 * Uses the new /medvantx/submit-sample-request endpoint
 */
export async function submitSampleRequest(
  requestID: string,
  selectedAddress: Address,
  signature: Signature
): Promise<SubmitFormResponse> {
  const payload = { requestID, selectedAddress, signature };
  console.log("[submitSampleRequest] Sending payload:", JSON.stringify(payload, null, 2));
  try {
    const response = await samplesApi.post<SubmitFormResponse>("/medvantx/submit-sample-request", payload);
    console.log("[submitSampleRequest] Response:", JSON.stringify(response.data, null, 2));
    return response.data;
  } catch (error: any) {
    console.error("[submitSampleRequest] Error:", {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
    });
    if (error.response?.data) {
      throw new Error(error.response.data.message || error.response.data.detail || `Server error (${error.response.status})`);
    }
    throw new Error("Network error. Please try again.");
  }
}

/**
 * Get the current status of a sample request
 * Uses the /medvantx/request-status/<requestID> endpoint
 */
export async function getRequestStatus(requestID: string): Promise<RequestStatusResponse> {
  try {
    const response = await samplesApi.get<RequestStatusResponse>(`/medvantx/request-status/${requestID}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("Request not found");
    }
    if (error.response?.data) {
      throw new Error(error.response.data.message || "Failed to get request status");
    }
    throw new Error("Network error. Please try again.");
  }
}

/**
 * Helper to extract requestID from URL query parameters
 * Expected URL format: /verify-phone?requestID=abc123 or /#/verify-phone?requestID=abc123
 */
export function getRequestIdFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  const requestIdParam = urlParams.get("requestID");
  
  // Also check hash params for HashRouter (/#/verify-phone?requestID=abc123)
  const hashParts = window.location.hash.split("?");
  const hashParams = new URLSearchParams(hashParts[1] || "");
  const hashRequestId = hashParams.get("requestID");
  
  return requestIdParam || hashRequestId || null;
}

export default {
  sendOTP,
  resendOTP,
  verifyOTP,
  getFormData,
  submitSampleRequest,
  getRequestStatus,
  getRequestIdFromUrl,
  resolveProducts,
};
